import { useState, useEffect, useRef } from 'react';
import { Search, X, Loader2, FileText } from 'lucide-react';
import * as mhrApi from '../../lib/api';
import type { MHRDocument, DocumentCategory } from '../../types/document';

interface DocumentSearchBarProps {
  patientId: string;
  onResultClick?: (documentId: string, page?: number) => void;
}

export function DocumentSearchBar({ patientId, onResultClick }: DocumentSearchBarProps) {
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<MHRDocument[]>([]);
  const [showResults, setShowResults] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!query.trim() || query.trim().length < 2) {
      setResults([]);
      setShowResults(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const data = await mhrApi.searchDocuments(patientId, { q: query.trim() });
        setResults(data.documents);
        setShowResults(true);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, patientId]);

  // Close results on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowResults(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div ref={containerRef} className="relative flex-1">
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results.length > 0 && setShowResults(true)}
          placeholder="Search document contents..."
          className="w-full pl-9 pr-8 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        />
        {searching && (
          <Loader2 size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 animate-spin" />
        )}
        {!searching && query && (
          <button
            onClick={() => { setQuery(''); setResults([]); setShowResults(false); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Results dropdown */}
      {showResults && results.length > 0 && (
        <div className="absolute z-10 top-full mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg max-h-80 overflow-y-auto">
          <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
            {results.length} document{results.length !== 1 ? 's' : ''} matched
          </div>
          {results.map((doc) => (
            // A div (not a button) so the inner per-page buttons are valid DOM
            // (a button may not contain interactive descendants).
            <div
              key={doc.id}
              role="button"
              tabIndex={0}
              onClick={() => {
                onResultClick?.(doc.id, doc.matching_pages?.[0]);
                setShowResults(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onResultClick?.(doc.id, doc.matching_pages?.[0]);
                  setShowResults(false);
                }
              }}
              className="w-full text-left px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-50 last:border-0 cursor-pointer"
            >
              <div className="flex items-center gap-2">
                <FileText size={14} className="text-gray-400 dark:text-gray-500 flex-shrink-0" />
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{doc.filename}</span>
              </div>
              {doc.matching_pages && doc.matching_pages.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1 ml-5">
                  {doc.matching_pages.slice(0, 8).map((page) => (
                    <button
                      key={page}
                      onClick={(e) => {
                        e.stopPropagation();
                        onResultClick?.(doc.id, page);
                        setShowResults(false);
                      }}
                      className="text-xs px-1.5 py-0.5 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 rounded hover:bg-primary-100 dark:hover:bg-primary-900/30"
                    >
                      p.{page}
                    </button>
                  ))}
                  {doc.matching_pages.length > 8 && (
                    <span className="text-xs text-gray-400 dark:text-gray-500">+{doc.matching_pages.length - 8} more</span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showResults && query.trim().length >= 2 && !searching && results.length === 0 && (
        <div className="absolute z-10 top-full mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg px-4 py-3 text-sm text-gray-500 dark:text-gray-400 text-center">
          No matching documents found
        </div>
      )}
    </div>
  );
}
